const normalizeBase = (value?: string) => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/\/+$/, "");
};

const ensureV1 = (base: string) => {
  const normalized = base.replace(/\/+$/, "");
  return /\/v1$/i.test(normalized) ? normalized : `${normalized}/v1`;
};

const getConfiguredBase = () =>
  normalizeBase(process.env.API_HOST) ??
  normalizeBase(process.env.BACKEND_HOST) ??
  normalizeBase(process.env.NEXT_PUBLIC_API_HOST) ??
  normalizeBase(process.env.NEXT_PUBLIC_API_BASE) ??
  normalizeBase(process.env.NEXT_PUBLIC_API_URL) ??
  normalizeBase(process.env.API_BASE);

export const getConfiguredApiBase = (): string => {
  const configured = getConfiguredBase();
  if (configured) {
    return ensureV1(configured);
  }

  // During `next build` (compilation + page-data collection) the backend host
  // is a runtime concern, not a build one — don't hard-fail the build when it's
  // absent. The throw still fires at runtime so a misconfigured deploy is caught
  // on the first request.
  const isBuildPhase = process.env.NEXT_PHASE === "phase-production-build";
  if (process.env.NODE_ENV === "production" && !isBuildPhase) {
    throw new Error(
      "Missing backend API host configuration. Set API_HOST, BACKEND_HOST, NEXT_PUBLIC_API_HOST, NEXT_PUBLIC_API_BASE, NEXT_PUBLIC_API_URL, or API_BASE.",
    );
  }

  return "http://127.0.0.1:3001/v1";
};

/**
 * Base URL for API requests.
 * In the browser we use the same-origin rewrite (/v1) so auth cookies are sent.
 * On the server we use the real backend URL.
 */
export const getApiBase = (): string => {
  if (typeof window !== "undefined") {
    // Use the Next.js API route proxy in the browser so auth cookies are
    // consistently translated into Authorization headers server-side.
    return `${window.location.origin}/api/v1`;
  }
  return getConfiguredApiBase();
};

export const getRealtimeApiOrigin = (): string => {
  const configured = getConfiguredBase();
  const fallback =
    typeof window !== "undefined" ? window.location.origin : getConfiguredApiBase();
  return (configured ?? fallback).replace(/\/v1\/?$/i, "").replace(/\/+$/, "");
};

export const buildApiUrl = (path: string) =>
  `${getApiBase()}${path.startsWith("/") ? path : `/${path}`}`;
